// components/FilterPopup.js
import React, { useEffect } from "react";
import { Box, Tabs, Tab, Typography, CircularProgress, IconButton, Link } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Context/AuthProvider";
import { useFilterPopup } from "./Filters/useFilterHooks";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  USTreasuryFilters,
  CorporatesFilters,
  MunicipalsFilters,
} from "./Filters/FilterTabContent";
import FilterNameModal from './Filters/FilterNameModal';

function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function FilterPopup() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  // Only initialize edit mode if we have valid filterData in location.state.
  const initialIsEdit = location.state && location.state.filterData ? location.state.isEdit : false;

  const {
    tabValue,
    setTabValue,
    criteriaCount,
    isCriteriaLoading,
    type,
    setType,
    state,
    setState,
    description,
    setDescription,
    issuer,
    setIssuer,
    issuerOptions,
    issuerValidationError,
    maturityMinDate,
    setMaturityMinDate,
    maturityMaxDate,
    setMaturityMaxDate,
    // New values for yieldtable preview/behavior
    maturityPreviewMinDate,
    maturityPreviewMaxDate,
    skipMaturityValidation,
    side,
    setSide,
    yieldToWorst,
    setYieldToWorst,
    quantity,
    setQuantity,
    price,
    setPrice,
    coupon,
    setCoupon,
    moodyRatings,
    setMoodyRatings,
    spRatings,
    setSpRatings,
    subTypeOptions,
    stateOptions,
    moodyRatingOptions,
    spRatingOptions,
    handleApplyFilter,
    handleClearFilter,
    handleSaveFilter,
    callable,
    setCallable,
    paymentFrequency,
    setPaymentFrequency,
    sector,
    setSector,
    nextCallDate,
    setNextCallDate,
    taxable,
    setTaxable,
    incomeSource,
    setIncomeSource,
    altMinTax,
    setAltMinTax,
    paymentFrequencyOptions,
    sectorOptions,
    incomeSourceOptions,
    isNameModalOpen,
    setIsNameModalOpen,
    filterName,
    setFilterName,
    handleActualSaveFilter,
    isEdit,
    setIsEdit,
    editFilterName,
    setEditFilterName,
    loadFilterData,
    isLoading,
    error,
    tabSwitchingEnabled,
    enableTabSwitching,
    brokerDealer,
    setBrokerDealer,
    brokerDealerOptions,
    securityCharge,
    setSecurityCharge,
    securityChargeOptions,
    // New flag
    isYieldTableSource,
    // NEW: rating preview exposures
    moodyPreviewFrom,
    moodyPreviewTo,
    spPreviewFrom,
    spPreviewTo,
  } = useFilterPopup(token, navigate, initialIsEdit);

  // Load filter data if coming from edit mode and clear location state after loading.
  useEffect(() => {
    if (location.state?.isEdit && location.state?.filterData) {
      const { filterName, filterData, savedFiltersId } = location.state;
      setIsEdit(true);
      setEditFilterName(filterName);
      (async () => {
        await loadFilterData(filterData, savedFiltersId);
        // Clear the location state so that refreshing doesn't preserve edit mode.
        navigate(location.pathname, { replace: true });
      })();
    }
  }, [location.state, location.pathname, navigate, setEditFilterName, setIsEdit]);

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (filterName.trim()) {
      handleActualSaveFilter(filterName);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography>Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton 
          onClick={handleGoBack} 
          sx={{ 
            mr: 1,
            p: 0.5 
          }}
          aria-label="back"
        >
          <ArrowBackIcon sx={{ fontSize: 24 }} />
        </IconButton>
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: { xs: "1rem", sm: "1.25rem" },
            fontWeight: 700,
            whiteSpace: "nowrap"
          }}
        >
          {isEdit ? 'Edit Filter' : 'Find Bonds or CDs'}
        </Typography>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => {
            if (isEdit && !tabSwitchingEnabled) {
              // Prevent tab switching in edit mode when not enabled.
              return;
            }
            setTabValue(newValue);
          }}
        >
          <Tab label="US Treasury" value={0} disabled={isEdit && !tabSwitchingEnabled && tabValue !== 0} />
          <Tab label="Corporates" value={1} disabled={isEdit && !tabSwitchingEnabled && tabValue !== 1} />
          <Tab label="Municipals" value={2} disabled={isEdit && !tabSwitchingEnabled && tabValue !== 2} />
          <Tab label="CDs" disabled />
          <Tab label="Agency/GSEs" disabled />
        </Tabs>
      </Box>
      {isEdit && !tabSwitchingEnabled && (
        <Box sx={{ mt: 1, ml: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ color: 'text.secondary' }}
          >
            Tab switching is disabled while editing.{' '}
            <Link 
              component="button"
              variant="caption"
              onClick={enableTabSwitching}
              sx={{ 
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                padding: 0,
                font: 'inherit'
              }}
            >
              Click here to enable tab switching
            </Link>
          </Typography>
        </Box>
      )}
      <Box sx={{ border: 1, borderColor: "divider", pl: 2 }}>
        <Typography variant="h6" sx={{ my: 2 }}>
          Key Search Criteria: {isCriteriaLoading ? <CircularProgress size={20} sx={{ ml: 1 }} /> : (criteriaCount === 0 ? <Typography component="span" color="error" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>None Available</Typography> : criteriaCount.toLocaleString("en-US"))}
        </Typography>
        {isEdit && editFilterName && (
          <Typography variant="subtitle1" sx={{ my: 2, color: 'text.secondary' }}>
            Editing filter: {editFilterName}
          </Typography>
        )}
        <CustomTabPanel value={tabValue} index={0}>
          <USTreasuryFilters
            type={type}
            onTypeChange={(e) => setType(e.target.value)}
            subTypeOptions={subTypeOptions}
            side={side}
            onSideChange={(e) => setSide(e.target.value)}
            brokerDealer={brokerDealer}
            onBrokerDealerChange={(e) => setBrokerDealer(e.target.value)}
            brokerDealerOptions={brokerDealerOptions}
            securityCharge={securityCharge}
            onSecurityChargeChange={setSecurityCharge}
            securityChargeOptions={securityChargeOptions}
            maturityMinDate={maturityMinDate}
            maturityMaxDate={maturityMaxDate}
            onMaturityMinDateChange={setMaturityMinDate}
            onMaturityMaxDateChange={setMaturityMaxDate}
            // New props for yieldtable preview/behavior
            maturityPreviewMinDate={maturityPreviewMinDate}
            maturityPreviewMaxDate={maturityPreviewMaxDate}
            skipMaturityValidation={skipMaturityValidation}
            // New: show Yield Cell tag if applicable
            showYieldCellTag={isYieldTableSource}
            yieldToWorst={yieldToWorst["Gov"]}
            onYieldToWorstChange={(key, value) =>
              setYieldToWorst((prev) => ({
                ...prev,
                Gov: { ...prev["Gov"], [key]: value },
              }))
            }
            quantity={quantity["Gov"]}
            onQuantityChange={(key, value) =>
              setQuantity((prev) => ({
                ...prev,
                Gov: { ...prev["Gov"], [key]: value },
              }))
            }
            price={price["Gov"]}
            onPriceChange={(key, value) =>
              setPrice((prev) => ({
                ...prev,
                Gov: { ...prev["Gov"], [key]: value },
              }))
            }
            coupon={coupon["Gov"]}
            onCouponChange={(key, value) =>
              setCoupon((prev) => ({
                ...prev,
                Gov: { ...prev["Gov"], [key]: value },
              }))
            }
            onApply={handleApplyFilter}
            onClear={handleClearFilter}
            onSave={handleSaveFilter}
            isEdit={isEdit}
            disableApply={criteriaCount === 0}
          />
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <CorporatesFilters
            moodyRatings={moodyRatings["Corp"]}
            onMoodyRatingsChange={(key, value) =>
              setMoodyRatings((prev) => ({
                ...prev,
                Corp: { ...prev["Corp"], [key]: value },
              }))
            }
            spRatings={spRatings["Corp"]}
            onSpRatingsChange={(key, value) =>
              setSpRatings((prev) => ({
                ...prev,
                Corp: { ...prev["Corp"], [key]: value },
              }))
            }
            moodyRatingOptions={moodyRatingOptions}
            spRatingOptions={spRatingOptions}
            description={description}
            onDescriptionChange={setDescription}
            issuer={issuer}
            onIssuerChange={setIssuer}
            issuerOptions={issuerOptions}
            issuerValidationError={issuerValidationError}
            side={side}
            onSideChange={(e) => setSide(e.target.value)}
            brokerDealer={brokerDealer}
            onBrokerDealerChange={(e) => setBrokerDealer(e.target.value)}
            brokerDealerOptions={brokerDealerOptions}
            securityCharge={securityCharge}
            onSecurityChargeChange={setSecurityCharge}
            securityChargeOptions={securityChargeOptions}
            maturityMinDate={maturityMinDate}
            maturityMaxDate={maturityMaxDate}
            onMaturityMinDateChange={setMaturityMinDate}
            onMaturityMaxDateChange={setMaturityMaxDate}
            // New props for yieldtable preview/behavior
            maturityPreviewMinDate={maturityPreviewMinDate}
            maturityPreviewMaxDate={maturityPreviewMaxDate}
            skipMaturityValidation={skipMaturityValidation}
            // New: show Yield Cell tag if applicable
            showYieldCellTag={isYieldTableSource}
            // NEW rating preview props
            moodyPreviewFrom={moodyPreviewFrom}
            moodyPreviewTo={moodyPreviewTo}
            spPreviewFrom={spPreviewFrom}
            spPreviewTo={spPreviewTo}
            yieldToWorst={yieldToWorst["Corp"]}
            onYieldToWorstChange={(key, value) =>
              setYieldToWorst((prev) => ({
                ...prev,
                Corp: { ...prev["Corp"], [key]: value },
              }))
            }
            quantity={quantity["Corp"]}
            onQuantityChange={(key, value) =>
              setQuantity((prev) => ({
                ...prev,
                Corp: { ...prev["Corp"], [key]: value },
              }))
            }
            price={price["Corp"]}
            onPriceChange={(key, value) =>
              setPrice((prev) => ({
                ...prev,
                Corp: { ...prev["Corp"], [key]: value },
              }))
            }
            coupon={coupon["Corp"]}
            onCouponChange={(key, value) =>
              setCoupon((prev) => ({
                ...prev,
                Corp: { ...prev["Corp"], [key]: value },
              }))
            }
            callable={callable}
            onCallableChange={setCallable}
            paymentFrequency={paymentFrequency}
            onPaymentFrequencyChange={setPaymentFrequency}
            sector={sector}
            onSectorChange={setSector}
            nextCallDate={nextCallDate}
            onNextCallDateChange={setNextCallDate}
            paymentFrequencyOptions={paymentFrequencyOptions}
            sectorOptions={sectorOptions}
            onApply={handleApplyFilter}
            onClear={handleClearFilter}
            onSave={handleSaveFilter}
            isEdit={isEdit}
            disableApply={criteriaCount === 0}
          />
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <MunicipalsFilters
            moodyRatings={moodyRatings["Muni"]}
            onMoodyRatingsChange={(key, value) =>
              setMoodyRatings((prev) => ({
                ...prev,
                Muni: { ...prev["Muni"], [key]: value },
              }))
            }
            spRatings={spRatings["Muni"]}
            onSpRatingsChange={(key, value) =>
              setSpRatings((prev) => ({
                ...prev,
                Muni: { ...prev["Muni"], [key]: value },
              }))
            }
            moodyRatingOptions={moodyRatingOptions}
            spRatingOptions={spRatingOptions}
            state={state}
            onStateChange={(e) => setState(e.target.value)}
            stateOptions={stateOptions}
            side={side}
            onSideChange={(e) => setSide(e.target.value)}
            brokerDealer={brokerDealer}
            onBrokerDealerChange={(e) => setBrokerDealer(e.target.value)}
            brokerDealerOptions={brokerDealerOptions}
            securityCharge={securityCharge}
            onSecurityChargeChange={setSecurityCharge}
            securityChargeOptions={securityChargeOptions}
            maturityMinDate={maturityMinDate}
            maturityMaxDate={maturityMaxDate}
            onMaturityMinDateChange={setMaturityMinDate}
            onMaturityMaxDateChange={setMaturityMaxDate}
            // New props for yieldtable preview/behavior
            maturityPreviewMinDate={maturityPreviewMinDate}
            maturityPreviewMaxDate={maturityPreviewMaxDate}
            skipMaturityValidation={skipMaturityValidation}
            // New: show Yield Cell tag if applicable
            showYieldCellTag={isYieldTableSource}
            // NEW rating preview props
            moodyPreviewFrom={moodyPreviewFrom}
            moodyPreviewTo={moodyPreviewTo}
            spPreviewFrom={spPreviewFrom}
            spPreviewTo={spPreviewTo}
            yieldToWorst={yieldToWorst["Muni"]}
            onYieldToWorstChange={(key, value) =>
              setYieldToWorst((prev) => ({
                ...prev,
                Muni: { ...prev["Muni"], [key]: value },
              }))
            }
            quantity={quantity["Muni"]}
            onQuantityChange={(key, value) =>
              setQuantity((prev) => ({
                ...prev,
                Muni: { ...prev["Muni"], [key]: value },
              }))
            }
            price={price["Muni"]}
            onPriceChange={(key, value) =>
              setPrice((prev) => ({
                ...prev,
                Muni: { ...prev["Muni"], [key]: value },
              }))
            }
            coupon={coupon["Muni"]}
            onCouponChange={(key, value) =>
              setCoupon((prev) => ({
                ...prev,
                Muni: { ...prev["Muni"], [key]: value },
              }))
            }
            callable={callable}
            onCallableChange={setCallable}
            paymentFrequency={paymentFrequency}
            onPaymentFrequencyChange={setPaymentFrequency}
            taxable={taxable}
            onTaxableChange={setTaxable}
            incomeSource={incomeSource}
            onIncomeSourceChange={setIncomeSource}
            altMinTax={altMinTax}
            onAltMinTaxChange={setAltMinTax}
            paymentFrequencyOptions={paymentFrequencyOptions}
            incomeSourceOptions={incomeSourceOptions}
            sector={sector}
            onSectorChange={setSector}
            sectorOptions={sectorOptions}
            onApply={handleApplyFilter}
            onClear={handleClearFilter}
            onSave={handleSaveFilter}
            isEdit={isEdit}
            disableApply={criteriaCount === 0}
          />
        </CustomTabPanel>
      </Box>

      
      <FilterNameModal
        open={isNameModalOpen}
        onClose={() => {
          setIsNameModalOpen(false);
        }}
        filterName={filterName}
        onFilterNameChange={(newName) => {
          setFilterName(newName);
        }}
        onSave={(name) => {
          handleActualSaveFilter(name);
        }}
      />
    </Box>
  );
}

export default FilterPopup;
